import { Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation, inject } from '@angular/core';
import { MediaObserver } from '@ngbracket/ngx-layout';

import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config';
import { CoreMediaService } from '@core/services/media';

import { coreConfig } from 'app/app-config';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from 'app/common/services/auth';
import { User } from 'app/common/models/user';

import { FeatherIconDirective } from '../../../../@core/directives/core-feather-icons/core-feather-icons';
import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.html',
    styleUrl: './navbar.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [RouterLink, FeatherIconDirective, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem]
})
export class NavbarComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _authService = inject(AuthService);
  private _coreConfigService = inject(CoreConfigService);
  private _coreMediaService = inject(CoreMediaService);
  private _coreSidebarService = inject(CoreSidebarService);
  private _mediaObserver = inject(MediaObserver);
  private _toastr = inject(ToastrService);
  _translateService = inject(TranslateService);

  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: User;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;

  // True while the reset-password code is being sent (prevents double clicks)
  public sendingResetCode = false;

  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == 'navbar-static-top' &&
      this.coreConfig.layout.type == 'horizontal'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   */
  constructor() {
    this._authService.currentUser.subscribe(x => (this.currentUser = x));

    this.languageOptions = {
      en: {
        title: 'English',
        flag: 'us'
      },
      tl: {
        title: 'Tagalog',
        flag: 'ph'
      },
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem('prevSkin');

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }

  /**
   * Reset password: emails an OTP to the logged-in user, then opens the
   * reset-password page with their email already filled in.
   * The user types the OTP from their inbox.
   */
  resetPassword(): void {
    if (this.sendingResetCode) {
      return;
    }

    const email = this.currentUser?.email;

    // No email available: fall back to the normal reset page
    if (!email) {
      this._router.navigate(['/auth/reset-password']);
      return;
    }

    this.sendingResetCode = true;

    // Swap for this._authService.adminForgotPassword(this.currentUser.id) if this
    // is the call that sends the link-with-code email in your backend.
    this._authService.clearSession();
    this._authService
      .forgotPassword(email)
      .pipe(finalize(() => (this.sendingResetCode = false)))
      .subscribe({
        next: (res: any) => {
          if (res?.succeeded === false || res?.Succeeded === false) {
            this._toastr.error(res.message ?? res.Message ?? 'Could not send the reset email.', 'Error', {
              positionClass: 'toast-top-center'
            });
            return;
          }

          this._toastr.success(`An OTP was sent to ${email}.`, 'Success', {
            positionClass: 'toast-top-center',
            timeOut: 4000
          });

          // Open the reset page with the email prefilled; the user enters the OTP.
          this._router.navigate(['/auth/reset-password'], { queryParams: { email } });
        },
        error: err => {
          const message = err?.error?.message ?? err?.error?.Message ?? 'Could not send the reset email. Please try again.';
          this._toastr.error(message, 'Error', { positionClass: 'toast-top-center' });
        }
      });
  }

  /**
   * Logout method
   */
  logout() {
    this._authService.clearSession();
    this._router.navigate(['/auth/login']);
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {

    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === 'horizontal';
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === 'vertical') {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === 'fixed-top') {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }
}